/*
 * Copyright (C) 2019 Toshiba Corporation
 * SPDX-License-Identifier: Apache-2.0
 */

import { Matrix, Point } from "pixi.js";
import { EShape } from "../e-shape";
import { EShapePoints } from "../e-shape-points";
import { EShapePointsFormatted } from "../e-shape-points-formatted";
import { EShapePointsFormatter } from "../e-shape-points-formatter";
import { EShapePointsMarkerContainer } from "../e-shape-points-marker-container";
import { EShapePointsMarkerContainerImplNoop } from "../e-shape-points-marker-container-impl-noop";
import { EShapePointsStyle } from "../e-shape-points-style";
import { EShapeResourceManagerSerialization } from "../e-shape-resource-manager-serialization";
import { EShapeGroupPropertyParent } from "./e-shape-group-property-parent";

export class EShapeGroupPoints implements EShapePoints {
	protected _parent: EShapeGroupPropertyParent;

	constructor(parent: EShapeGroupPropertyParent) {
		this._parent = parent;
	}

	get length(): number {
		const children = this._parent.children;
		if (0 < children.length) {
			const points = children[children.length - 1].points;
			if (points != null) {
				return points.length;
			}
		}
		return 0;
	}

	get plength(): number {
		const children = this._parent.children;
		if (0 < children.length) {
			const points = children[children.length - 1].points;
			if (points != null) {
				return points.plength;
			}
		}
		return 0;
	}

	get id(): number {
		const children = this._parent.children;
		if (0 < children.length) {
			const points = children[children.length - 1].points;
			if (points != null) {
				return points.id;
			}
		}
		return 0;
	}

	get values(): number[] {
		const children = this._parent.children;
		if (0 < children.length) {
			const points = children[children.length - 1].points;
			if (points != null) {
				return points.values;
			}
		}
		return [];
	}

	set values(values: number[]) {
		const children = this._parent.children;
		for (let i = 0, imax = children.length; i < imax; ++i) {
			const points = children[i].points;
			if (points != null) {
				points.values = values;
			}
		}
	}

	get segments(): number[] {
		const children = this._parent.children;
		if (0 < children.length) {
			const points = children[children.length - 1].points;
			if (points != null) {
				return points.segments;
			}
		}
		return [];
	}

	set segments(segments: number[]) {
		const children = this._parent.children;
		for (let i = 0, imax = children.length; i < imax; ++i) {
			const points = children[i].points;
			if (points != null) {
				points.segments = segments;
			}
		}
	}

	get style(): EShapePointsStyle {
		const children = this._parent.children;
		if (0 < children.length) {
			const points = children[children.length - 1].points;
			if (points != null) {
				return points.style;
			}
		}
		return EShapePointsStyle.NONE;
	}

	set style(style: EShapePointsStyle) {
		const children = this._parent.children;
		for (let i = 0, imax = children.length; i < imax; ++i) {
			const points = children[i].points;
			if (points != null) {
				points.style = style;
			}
		}
	}

	get marker(): EShapePointsMarkerContainer {
		const children = this._parent.children;
		for (let i = 0, imax = children.length; i < imax; ++i) {
			const points = children[i].points;
			if (points != null) {
				return points.marker;
			}
		}
		return EShapePointsMarkerContainerImplNoop.getInstance();
	}

	getMarker(): EShapePointsMarkerContainer | undefined {
		const children = this._parent.children;
		for (let i = 0, imax = children.length; i < imax; ++i) {
			const points = children[i].points;
			if (points != null) {
				return points.getMarker();
			}
		}
		return undefined;
	}

	get formatter(): EShapePointsFormatter | null {
		const children = this._parent.children;
		if (0 < children.length) {
			const points = children[children.length - 1].points;
			if (points != null) {
				return points.formatter;
			}
		}
		return null;
	}

	set formatter(formatter: EShapePointsFormatter | null) {
		const children = this._parent.children;
		for (let i = 0, imax = children.length; i < imax; ++i) {
			const points = children[i].points;
			if (points != null) {
				points.formatter = formatter;
			}
		}
	}

	get formatted(): EShapePointsFormatted {
		const children = this._parent.children;
		if (0 < children.length) {
			const points = children[children.length - 1].points;
			if (points != null) {
				return points.formatted;
			}
		}
		return this;
	}

	onSizeChange(): void {
		// DO NOTHING
	}

	copy(source: EShapePoints): this {
		const children = this._parent.children;
		for (let i = 0, imax = children.length; i < imax; ++i) {
			const points = children[i].points;
			if (points != null) {
				points.copy(source);
			}
		}
		return this;
	}

	set(values?: number[], segments?: number[], style?: EShapePointsStyle): this {
		const children = this._parent.children;
		for (let i = 0, imax = children.length; i < imax; ++i) {
			const points = children[i].points;
			if (points != null) {
				points.set(values, segments, style);
			}
		}
		return this;
	}

	clone(parent: EShape): EShapeGroupPoints {
		return new EShapeGroupPoints(parent);
	}

	toPoints(transform: Matrix): Point[] {
		const children = this._parent.children;
		for (let i = 0, imax = children.length; i < imax; ++i) {
			const points = children[i].points;
			if (points != null) {
				return points.toPoints(transform);
			}
		}
		return [];
	}

	serialize(manager: EShapeResourceManagerSerialization): number {
		const children = this._parent.children;
		for (let i = 0, imax = children.length; i < imax; ++i) {
			const points = children[i].points;
			if (points != null) {
				return points.serialize(manager);
			}
		}
		return -1;
	}
}
